import { AllSelection, TextSelection } from 'prosemirror-state'
import { Node } from 'prosemirror-model'

import type { Command } from 'common/Prose/Commands'
import { INDENT_DIRECTION } from 'common/Prose/Commands/constants'
import { preserveSelection } from 'common/Prose/utils'
import { MAX_LIST_DEPTH, schema } from 'prose-core'
import { toggleStateStore } from '../nodeViews/ToggleList/store'
import { isListItem } from '../utils/isListItem'

/**
 * Attention: This is a factory that returns a command
 *
 * If you are inside a list, indent or unindent (according to `DIRECTION`) all selected list items
 */
export const changeIndentation: (direction: INDENT_DIRECTION) => Command =
  (direction, allowEmptySelection = true) =>
  (state, dispatch) => {
    let {
      tr,
      doc,
      selection: { $anchor, $from, $head, $to, from, to },
    } = state

    // eslint-disable-next-line almanac-editor/dont-break-pm-dry-run
    if (!dispatch) return false

    // NOTE: all selections are around the doc node, users probably want to go
    // from before the first node to after the last node though, so we
    // "correct" that here
    if (state.selection instanceof AllSelection) {
      if (doc.nodeSize <= 3) return false

      from = 1
      $from = doc.resolve(from)
    }

    let selectedNode: Node
    let maybeLeadingToggleBlockPosition: number
    let maybeWrappingToggleBlockPosition: number
    if (isListItem($from.node(-1)?.type)) {
      selectedNode = $from.node(-1)
      maybeLeadingToggleBlockPosition = $from.before(-1) - 1
      maybeWrappingToggleBlockPosition = $from.before(-1)
    } else if ($from.node(-1)?.type === schema.nodes.summary) {
      selectedNode = $from.node(-2)
      maybeLeadingToggleBlockPosition = maybeWrappingToggleBlockPosition = $from.before(-2)
    } else if ($from.nodeAfter || $from.node().isText) {
      selectedNode = $from.parent
      maybeLeadingToggleBlockPosition = maybeWrappingToggleBlockPosition = $from.before()
    } else {
      selectedNode = $from.node()
      maybeLeadingToggleBlockPosition = maybeWrappingToggleBlockPosition = $from.before()
    }
    const $maybeLeadingToggleBlockPosition = doc.resolve(maybeLeadingToggleBlockPosition)
    const $maybeWrappingToggleBlockPosition = doc.resolve(maybeWrappingToggleBlockPosition)

    const wrapperNode = $from.node($from.depth - 1)

    // NOTE: don't indent text wrap nodes in tables if the selection starts on them, instead allow tab to jump to next cell
    if (
      selectedNode.type.groups.includes('textWrap') &&
      (wrapperNode.type === schema.nodes.table_cell || wrapperNode.type === schema.nodes.table_header)
    )
      return false

    // NOTE: If we selected stuff after a toggle block and indent, put it into the toggle block
    if (
      $maybeLeadingToggleBlockPosition.nodeBefore?.type === schema.nodes.toggle_list &&
      direction === INDENT_DIRECTION.INDENT
    ) {
      let afterPos: number

      toggleStateStore.setState($maybeLeadingToggleBlockPosition.nodeBefore.attrs.blockId, true)
      if ([schema.nodes.list_item].includes($to.node(-1)?.type as any)) {
        afterPos = $to.after(-1)
      } else if ($to.node(-2)?.type === schema.nodes.toggle_list) {
        afterPos = $to.after(-2)
      } else {
        afterPos = $to.after()
      }
      const $afterPos = doc.resolve(afterPos)

      if ($afterPos.nodeBefore?.type === schema.nodes.list_item && $afterPos.nodeAfter) tr.split($afterPos.pos)
      const content = tr.doc.slice(maybeLeadingToggleBlockPosition, $afterPos.pos).content
      tr.delete(maybeLeadingToggleBlockPosition, $afterPos.pos)

      // NOTE: if the toggle block only has an empty node as details, replace that
      if (
        $maybeLeadingToggleBlockPosition.nodeBefore?.childCount === 2 &&
        $maybeLeadingToggleBlockPosition.nodeBefore?.lastChild?.childCount === 0
      ) {
        tr.replaceWith(maybeLeadingToggleBlockPosition - 3, maybeLeadingToggleBlockPosition - 1, content)
        tr.setSelection(
          TextSelection.create(
            tr.doc,
            maybeLeadingToggleBlockPosition - 3 + ($anchor.pos - maybeLeadingToggleBlockPosition),
            maybeLeadingToggleBlockPosition - 3 + ($head.pos - maybeLeadingToggleBlockPosition),
          ),
        )
      } else {
        tr.insert(maybeLeadingToggleBlockPosition - 1, content)
        tr.setSelection(
          TextSelection.create(
            tr.doc,
            maybeLeadingToggleBlockPosition - 1 + ($anchor.pos - maybeLeadingToggleBlockPosition),
            maybeLeadingToggleBlockPosition - 1 + ($head.pos - maybeLeadingToggleBlockPosition),
          ),
        )
      }
    } else if (
      // NOTE: if we deindent stuff in a toggle block that is itself not indented, remove it and everything after it from the toggle block
      ($maybeWrappingToggleBlockPosition.node().type === schema.nodes.toggle_list ||
        ($maybeWrappingToggleBlockPosition.node().type.groups.includes('list') &&
          $maybeWrappingToggleBlockPosition.node(-1).type === schema.nodes.toggle_list)) &&
      direction === INDENT_DIRECTION.UNINDENT &&
      !selectedNode.attrs.depth
    ) {
      // NOTE: lists need slightly different positions so we don't leave list items behind or insert list items without a list
      if ($maybeWrappingToggleBlockPosition.nodeAfter?.type === schema.nodes.list_item) {
        let split = false

        if ($maybeWrappingToggleBlockPosition.nodeBefore) {
          tr.split(maybeWrappingToggleBlockPosition)
          split = true
        }

        // NOTE: a split inserts another list token before, so we are already before the list we split
        const content = tr.doc.slice(
          maybeWrappingToggleBlockPosition - (split ? -1 : 1),
          tr.mapping.map($maybeWrappingToggleBlockPosition.end(-1)),
        ).content

        tr.delete(maybeWrappingToggleBlockPosition - 1, tr.mapping.map($maybeWrappingToggleBlockPosition.end(-1)))
        const insertPos = tr.mapping.map($maybeWrappingToggleBlockPosition.after()) + 1
        tr.insert(insertPos, content)

        tr.setSelection(
          TextSelection.create(
            tr.doc,
            insertPos + ($anchor.pos - maybeWrappingToggleBlockPosition) + (split ? 3 : 1), // NOTE: in the case of a split we get two extra list tokens, the closing one for the list before and the opening one for the split off list
            insertPos + ($head.pos - maybeWrappingToggleBlockPosition) + (split ? 3 : 1),
          ),
        )
      } else {
        const content = tr.doc.slice(maybeWrappingToggleBlockPosition, $maybeWrappingToggleBlockPosition.end()).content

        tr.delete(maybeWrappingToggleBlockPosition, $maybeWrappingToggleBlockPosition.end())
        const insertPos = tr.mapping.map($maybeWrappingToggleBlockPosition.after())
        tr.insert(insertPos, content)

        tr.setSelection(
          TextSelection.create(
            tr.doc,
            insertPos + ($anchor.pos - maybeWrappingToggleBlockPosition),
            insertPos + ($head.pos - maybeWrappingToggleBlockPosition),
          ),
        )
      }
    } else {
      // NOTE: otherwise indent node by node
      let selectedNodeFound = false

      doc.nodesBetween(from, to, (node, position, parent) => {
        if (node === selectedNode) selectedNodeFound = true

        if (selectedNodeFound) {
          // NOTE: treat list items and text wrapping nodes
          if (isListItem(node.type) || node.type.groups.includes('textWrap')) {
            // NOTE: don't indent nodes in toggle blocks
            if ([schema.nodes.summary].includes(parent.type as any)) return false

            // NOTE: if we indent, check if the node is less than the max list depth, if we unindent, check if it's not yet 0 already
            if (direction === INDENT_DIRECTION.INDENT ? node.attrs.depth < MAX_LIST_DEPTH : node.attrs.depth > 0) {
              tr.setNodeMarkup(position, node.type, {
                ...node.attrs,
                depth: parseInt(node.attrs.depth, 10) + direction,
              }) // NOTE: parseint is here b/c some nodes are in the database with a string depth b/c of a past bug

              // only indent same-level nodes
              return false
            }
          }
        }

        return true
      })

      if (tr.docChanged) preserveSelection(tr, state)
    }

    if (tr.docChanged) {
      dispatch(tr)

      return true
    }

    return false
  }
